import Vue from 'vue';

import OfferEvent from '@core/models/offer/OfferEvent';
import Struct from '@core/models/struct/Struct';
import Category from '@models/struct/Category';
import Market from '@models/struct/Market';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import OddType from '@models/struct/OddType';
import Sport from '@models/struct/Sport';
import Tournament from '@models/struct/Tournament';
import StoreUtil from '@store/StoreUtil';
import deepFreeze from '@utils/dataTransformation/deepFreeze';

import { mergeMatch } from './helpers';
import { ISportOfferState } from './index';
import * as types from './mutationTypes';
import { ISuperOfferCollections } from './types';
import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import { CategoryWithCompetitions } from '@core/models/struct/CategoryWithCompetitions';

export default {
    [types.SET_STRUCT](state: ISportOfferState, struct: Struct): void {
        state.sports = deepFreeze(struct.sports);
        state.categories = deepFreeze(struct.categories);
        state.tournaments = deepFreeze(struct.tournaments);
        state.markets = deepFreeze(struct.markets);
        state.oddTypes = deepFreeze(struct.oddTypes);
        // in V2 this is empty, so skip updating because otherwise
        // we are going to empty marketGroupInfos
        if (Object.keys(struct.marketGroupInfos).length) {
            state.marketGroupInfos = deepFreeze(struct.marketGroupInfos);
        }
        state.marketTree = deepFreeze(struct.marketTree);
        state.isStructFetched = true;
    },
    [types.SET_SPORTS](state: ISportOfferState, sports: Record<number, Sport>): void {
        state.sports = deepFreeze(sports);
    },
    [types.SET_CATEGORIES](state: ISportOfferState, categories: Record<number, Category>): void {
        state.categories = deepFreeze(categories);
    },
    [types.SET_TOURNAMENTS](state: ISportOfferState, tournaments: Record<number, Tournament>): void {
        state.tournaments = deepFreeze(tournaments);
    },
    [types.SET_MARKETS](state: ISportOfferState, markets: Record<number, Market>): void {
        state.markets = deepFreeze(markets);
    },
    [types.SET_ODD_TYPES](state: ISportOfferState, oddTypes: Record<number, OddType>): void {
        state.oddTypes = deepFreeze(oddTypes);
    },
    [types.SET_MARKET_GROUP_INFOS](state: ISportOfferState, marketGroupInfos: Record<number, MarketGroupInfo>): void {
        state.marketGroupInfos = deepFreeze(marketGroupInfos);
    },
    [types.SET_EVENTS](state: ISportOfferState, events: OfferEvent[]): void {
        events.forEach((event) => {
            freezeOdds(event);
            if (state.events[event.id]) {
                Vue.set(
                    state.events,
                    event.id.toString(),
                    // mergeMatch freezes object!
                    mergeMatch(
                        state.events[event.id],
                        event,
                    ),
                );
            } else {
                Vue.set(
                    state.events,
                    event.id.toString(),
                    Object.freeze(event),
                );
            }
        });
    },
    [types.SET_EVENT](state: ISportOfferState, event: OfferEvent): void {
        freezeOdds(event);
        Vue.set(
            state.events,
            event.id.toString(),
            Object.freeze(event),
        );
    },
    [types.INCREMENT_ALL_CHANGES_ID](state: ISportOfferState): void {
        state.allChangesIncrementId = (state.allChangesIncrementId + 1) % 2;
    },
    [types.REMOVE_EVENTS](state: ISportOfferState, eventIds: number[]): void {
        eventIds.forEach((id) => Vue.delete(state.events, id.toString()));
    },
    [types.REMOVE_EVENT](state: ISportOfferState, eventId: number): void {
        Vue.delete(state.events, eventId.toString());
    },
    [types.SET_TOP_TEN_EVENTS](state: ISportOfferState, eventsIds: number[]): void {
        state.topTenEvents = eventsIds;
    },
    [types.SET_TOP_TEN_EVENT](state: ISportOfferState, eventId: number): void {
        state.topTenEvents.push(eventId);
    },
    [types.SET_TOP_EVENT_IDS](state: ISportOfferState, eventsIds: number[]): void {
        state.topEventIds = eventsIds;
    },
    [types.SET_PROMOTIONAL_EVENTS](state: ISportOfferState, promotionalEvents: ISuperOfferCollections): void {
        state.promotionalEvents = promotionalEvents;
    },

    [types.UPDATE_EVENT](state: ISportOfferState, event: OfferEvent): void {
        freezeOdds(event);
        if (state.events[event.id]) {
            state.events[event.id] = mergeMatch(
                state.events[event.id],
                event,
            ) as OfferEvent;
        }
    },
    [types.SET_IS_OFFER_STARTING]: StoreUtil.createSimpleMutator('isOfferStarting'),
    [types.SET_IS_OFFER_READY]: StoreUtil.createSimpleMutator('isOfferReady'),
    [types.SET_LIVE_EVENTS_COUNT]: StoreUtil.createSimpleMutator('liveEventsCount'),
    [types.SET_ARE_SUBSCRIPTIONS_SET]: StoreUtil.createSimpleMutator('areSubscriptionsSet'),
    [types.SET_IS_EVENTS_SUBSCRIPTION_SET]: StoreUtil.createSimpleMutator('isEventsSubscriptionSet'),
    [types.SET_IS_OFFER_FETCHED]: StoreUtil.createSimpleMutator('isOfferFetched'),
    [types.SET_IS_OFFER_FETCHED_BY_SPORT_ID]: (state: ISportOfferState, sportId: number) => {
        state.isOfferFetchedBySport = {
            ...state.isOfferFetchedBySport,
            [sportId]: true,
        };
    },
    [types.SET_ARE_LIVE_EVENTS_FETCHED]: StoreUtil.createSimpleMutator('areLiveEventsFetched'),
    [types.SET_IS_SUPER_OFFER_FETCHED]: StoreUtil.createSimpleMutator('isSuperOfferFetched'),
    [types.SET_ARE_TOP_TEN_EVENTS_FETCHED]: StoreUtil.createSimpleMutator('areTopTenEventsFetched'),
    [types.SET_ARE_TOP_EVENT_IDS_FETCHED]: StoreUtil.createSimpleMutator('areTopEventIdsFetched'),
    [types.SET_ODD_VALUE_DYNAMICS]: StoreUtil.createSimpleMutator('oddValueDynamics'),
    [types.SET_IS_COMPETITION_TREE_FETCHED_FOR_SPORT_ID]: (state: ISportOfferState, payload: { sportId: number; isFetched: boolean }): void => {
        Vue.set(state.isCompetitionTreeFetchBySportId, payload.sportId, payload.isFetched);
    },
    [types.SET_COMPETITION_TREE_FOR_SPORT_ID]: (state: ISportOfferState, payload: { sportId: number; competitionTree: CategoryWithCompetitions[] }): void => {
        Vue.set(state.competitionTreeBySportId, payload.sportId, payload.competitionTree);
    },
    [types.SET_IS_FIRST_DAILY_OFFER_SYNC]: createSimpleMutator<ISportOfferState, 'isFirstDailyOfferSync'>('isFirstDailyOfferSync'),
    [types.SET_IS_FIRST_STRUCT_SYNC]: createSimpleMutator<ISportOfferState, 'isFirstStructSync'>('isFirstStructSync'),
    [types.SET_SPORT_DATES_WITH_ACTIVE_OFFER]: (state: ISportOfferState, payload: { sportId: number; dates: Date[] }): void => {
        Vue.set(state.sportDatesWithActiveOffer, payload.sportId, payload.dates);
    },
    [types.SET_SPORTS_WITH_EVENTS]: createSimpleMutator<ISportOfferState, 'sportsWithEvents'>('sportsWithEvents'),
    [types.SET_POPULAR_SUPERBETS]: createSimpleMutator<ISportOfferState, 'popularSuperbets'>('popularSuperbets'),
    [types.SET_POPULAR_SUPERBETS_V2]: createSimpleMutator<ISportOfferState, 'popularSuperbetsV2'>('popularSuperbetsV2'),
};

function freezeOdds(match: OfferEvent) {
    match.setFrozenOdds(match.getOdds().map((odd) => Object.freeze(odd)));
}
